import { createResolver, naturalCompare, type NoteWithProps } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useNewNote } from '../components/NewNoteDialog';
import { NoteLink } from '../components/NoteLink';
import { formatGold } from '../components/Properties';
import { useCampaign, useInvalidateCampaign, useNoteIndex, useTypes } from '../hooks';
import { t } from '../i18n';
import { classNames } from '../util';

const NO_STATUS = '';

/** `[[Ziel|Alias]]` → Ziel */
function linkTarget(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = /^\s*\[\[([^\]|#]+)(?:#[^\]|]*)?(?:\|[^\]]*)?\]\]\s*$/.exec(v);
  return m ? m[1]!.trim() : null;
}

/** Quests als Kanban nach Status; Karten per Ziehen oder Pfeilen verschieben. */
export function QuestsPage() {
  const { cid = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const newNote = useNewNote();
  const campaign = useCampaign(cid);
  const gm = !!campaign.data && campaign.data.role !== 'player';
  const types = useTypes(cid);
  const index = useNoteIndex(cid);
  const invalidate = useInvalidateCampaign(cid);
  const quests = useQuery({ queryKey: ['notesOfType', cid, 'quest'], queryFn: () => api.notesOfType(cid, 'quest') });
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t('quests.title')} · Kartothek`;
  }, []);

  const statuses = useMemo(() => {
    const field = types.byKey.get('quest')?.fields.find((f) => f.key === 'status');
    const base = field?.options?.length ? field.options : ['offen', 'aktiv', 'erledigt', 'gescheitert'];
    const extra = [...new Set((quests.data ?? []).map((q) => String(q.frontmatter.status ?? '')).filter((s) => s && !base.includes(s)))];
    return [...base, ...extra];
  }, [types.byKey, quests.data]);

  const resolver = useMemo(
    () => createResolver((index.data ?? []).map((n) => ({ id: n.id, folder: n.folder, title: n.title }))),
    [index.data],
  );

  const columns = useMemo(() => {
    const map = new Map<string, NoteWithProps[]>([[NO_STATUS, []], ...statuses.map((s) => [s, []] as [string, NoteWithProps[]])]);
    for (const q of quests.data ?? []) {
      const s = String(q.frontmatter.status ?? '');
      map.get(statuses.includes(s) ? s : NO_STATUS)!.push(q);
    }
    for (const list of map.values()) list.sort((a, b) => naturalCompare(a.title, b.title));
    return map;
  }, [quests.data, statuses]);

  const move = async (q: NoteWithProps, status: string) => {
    if (String(q.frontmatter.status ?? '') === status) return;
    // Optimistisch verschieben
    qc.setQueryData<NoteWithProps[]>(['notesOfType', cid, 'quest'], (old) =>
      old?.map((x) => (x.id === q.id ? { ...x, frontmatter: { ...x.frontmatter, status: status || undefined } } : x)),
    );
    await (status ? api.patchFrontmatter(cid, q.id, { status }) : api.patchFrontmatter(cid, q.id, {}, ['status']));
    void qc.invalidateQueries({ queryKey: ['notesOfType', cid, 'quest'] });
    void qc.invalidateQueries({ queryKey: ['note', cid, q.id] });
  };

  const renderLink = (v: unknown) => {
    const target = linkTarget(v);
    if (!target) return typeof v === 'string' ? v : null;
    const r = resolver.resolve(target);
    return r?.type === 'note' ? (
      <NoteLink cid={cid} noteId={r.id}>
        {target}
      </NoteLink>
    ) : (
      <span className="wikilink unresolved">{target}</span>
    );
  };

  const visibleColumns = [...columns.entries()].filter(([s, list]) => s !== NO_STATUS || list.length > 0);

  return (
    <div className="kanban-page">
      <h1>{t('quests.title')}</h1>
      {quests.data?.length === 0 && <p className="muted">{t('quests.none')}</p>}
      <div className="kanban">
        {visibleColumns.map(([status, list]) => (
          <section
            key={status || '-'}
            className={classNames('kanban-col', over === status && 'over')}
            onDragOver={(e) => {
              if (!gm) return;
              e.preventDefault();
              setOver(status);
            }}
            onDragLeave={() => setOver((o) => (o === status ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const q = quests.data?.find((x) => x.id === dragId);
              if (q) void move(q, status);
            }}
          >
            <h2>
              {status || t('quests.noStatus')} <span className="count">{list.length}</span>
            </h2>
            {list.map((q) => {
              const i = statuses.indexOf(String(q.frontmatter.status ?? ''));
              return (
                <article
                  key={q.id}
                  className="kanban-card"
                  draggable={gm}
                  onDragStart={() => setDragId(q.id)}
                  onDragEnd={() => setDragId(null)}
                >
                  <NoteLink cid={cid} noteId={q.id}>
                    <strong>{q.title}</strong>
                  </NoteLink>
                  <div className="kanban-meta small">
                    {q.frontmatter.giver !== undefined && (
                      <div>
                        <span className="muted">{t('quests.giver')}:</span> {renderLink(q.frontmatter.giver)}
                      </div>
                    )}
                    {q.frontmatter.location !== undefined && (
                      <div>
                        <span className="muted">{t('quests.location')}:</span> {renderLink(q.frontmatter.location)}
                      </div>
                    )}
                    {q.frontmatter.reward_gp !== undefined && (
                      <div>
                        <span className="muted">{t('quests.reward')}:</span> {formatGold(q.frontmatter.reward_gp)}
                      </div>
                    )}
                  </div>
                  {gm && (
                    <div className="kanban-move">
                      <button disabled={i <= 0} onClick={() => void move(q, statuses[i - 1]!)} title={t('quests.moveLeft')}>
                        ◀
                      </button>
                      <button disabled={i >= statuses.length - 1} onClick={() => void move(q, statuses[i + 1] ?? statuses[0]!)} title={t('quests.moveRight')}>
                        ▶
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
            {gm && status !== NO_STATUS && (
              <button
                className="kanban-add"
                onClick={() =>
                  newNote.open({
                    typeKey: 'quest',
                    folder: 'Quests',
                    onCreated: async (id) => {
                      await api.patchFrontmatter(cid, id, { status });
                      invalidate();
                      void qc.invalidateQueries({ queryKey: ['notesOfType', cid, 'quest'] });
                      navigate(`/c/${cid}/n/${id}?edit=1`);
                    },
                  })
                }
              >
                + {t('quests.new')}
              </button>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
